// ============================================================================
// LE PANNEAU DE L'ÉLÉMENT SÉLECTIONNÉ — position, taille, alignement, et les deux indicateurs.
//
// ⚠️ DEUX PANNEAUX, PAS UN PANNEAU GRISÉ. Sur un gabarit standard, on RESTITUE des valeurs ; sur un
// gabarit libre, on offre des contrôles. Un champ non modifiable n'est pas un champ (doctrine
// `ui-web`) : il prend le focus pour ne rien accepter, et un segmenté inerte est un bouton qui fait
// semblant (§23.0). Les deux formes ne partagent donc que les indicateurs — qui, eux, disent la même
// chose dans les deux cas.
// ============================================================================
import {
  clampPosition, clampSize, elementWidthMm, fitInBox, FIELD_BARCODE, nudgeStepMm,
  templateBarcodeQuality, MIN_BARCODE_WIDTH_MM, MIN_TEXT_WIDTH_MM,
  type LabelAlign, type LabelElement, type LabelFieldKey, type LabelTemplate,
} from '@orgazone/core-catalog';
import { Badge, Button, Field, Icon, ReadOnlyValue, Segmented, showToast, Tooltip, type IconName } from '@orgazone/ui-web';
import { fieldLabel } from '../vocabulary';
import { t } from '../i18n';

/**
 * Le nom à l'écran d'un champ imprimable — le mot que CE magasin emploie, lu au moment du rendu.
 * ⚠️ UNE FONCTION, PAS UNE TABLE : une constante de module figerait le mot de la plateforme avant que
 *    le contexte magasin ne soit lu, et le caviste lirait « Saison » pour toujours, sans erreur.
 *    Les CLÉS viennent du cœur, jamais d'ici.
 * ⚠️ `barcode` et `gencode` sont deux REPRÉSENTATIONS du même champ du catalogue : le mot est celui
 *    du gencode, la parenthèse dit laquelle des deux on imprime.
 */
export function labelFieldName(field: LabelFieldKey): string {
  switch (field) {
    case 'desig': return fieldLabel('name');
    case 'code': return fieldLabel('articleCode');
    case 'price': return fieldLabel('salePriceInclTax');
    case 'barcode': return t('{v} (graphique)', { v: fieldLabel('gencode') });
    case 'gencode': return t('{v} (chiffres)', { v: fieldLabel('gencode') });
    case 'size': return fieldLabel('size');
    case 'color': return fieldLabel('color');
    case 'brand': return fieldLabel('brand');
    case 'sup': return fieldLabel('supplier');
    case 'origin': return fieldLabel('originCountry');
    case 'season': return fieldLabel('season');
  }
}

/** Millimètres à la française, une décimale — la façon dont l'éditeur affiche toutes ses mesures. */
export const mm = (v: number) => v.toFixed(1).replace('.', ',');

/** Une saisie « 12,5 » en nombre. `null` = pas un nombre, on ne touche à rien. */
export function parseMm(raw: string): number | null {
  const v = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(v) ? Math.round(v * 10) / 10 : null;
}

/**
 * UN CHAMP EN MILLIMÈTRES — il montre toujours la mesure que le gabarit PORTE, jamais une saisie qu'il n'a pas prise.
 *
 * ⚠️ NON CONTRÔLÉ, parce que la planche change la mesure au glisser : sa clé porte la valeur, et à la sortie il se
 *    réécrit avec ce qui a été APPLIQUÉ. Sans ça, « 999 » restait affiché sur une largeur ramenée à sa borne, et « abc »
 *    sur une mesure inchangée — l'écran montrait un réglage que l'étiquette n'avait pas.
 * ⚠️ UN REFUS ET UNE BORNE SE DISENT : la valeur qui revient seule dans la case, sans un mot, ressemble à une saisie
 *    perdue.
 * ⚠️ SORTIR SANS RIEN CHANGER N'ÉCRIT RIEN : traverser les champs au clavier ne doit pas relancer un enregistrement.
 */
export function MmField({ label, value, clamp, onCommit, onEmpty, placeholder }: {
  label: string;
  /** La mesure en vigueur — `undefined` pour une mesure « libre ». */
  value: number | undefined;
  /** La mesure que le gabarit PORTERAIT pour cette saisie — bornée s'il le faut. Sans effet : elle ne fait que calculer. */
  clamp: (v: number) => number;
  /**
   * Écrit la mesure retenue.
   * ⚠️ APPELÉ SEULEMENT QUAND ELLE CHANGE : « 999 » ramené à une largeur déjà au maximum ne change rien, et ne doit pas
   *    relancer un enregistrement identique.
   */
  onCommit: (v: number) => void;
  /** Un champ VIDÉ : sans ce rappel, vider est une saisie illisible comme une autre. */
  onEmpty?: () => void;
  placeholder?: string;
}) {
  const shown = value === undefined ? '' : mm(value);
  return (
    <Field
      label={label}
      labelStyle="dense"
      key={shown}
      defaultValue={shown}
      placeholder={placeholder}
      style={MONO}
      onBlur={(e) => {
        const raw = e.target.value.trim();
        if (raw === shown) return;
        if (!raw && onEmpty) { if (value !== undefined) onEmpty(); return; }
        const v = parseMm(raw);
        if (v === null) {
          e.target.value = shown;
          showToast(value === undefined
            ? t('« {saisie} » n’est pas une mesure — le champ reste libre.', { saisie: raw })
            : t('« {saisie} » n’est pas une mesure — la valeur reste {v} mm.', { saisie: raw, v: shown }), 'warn');
          return;
        }
        const applied = clamp(v);
        // ⚠️ LA CASE MONTRE LA MESURE RETENUE, MÊME QUAND RIEN NE CHANGE : une mesure qui change remonte le champ d'elle-
        //    même (sa clé la porte), mais « 25 » tapé pour 25,0, ou « 999 » ramené à un maximum déjà atteint, laissaient
        //    la saisie affichée sur un gabarit qui portait autre chose.
        e.target.value = mm(applied);
        if (applied !== value) onCommit(applied);
        if (applied !== v) showToast(t('Ramené à {v} mm — la valeur tapée sortait des bornes permises.', { v: mm(applied) }), 'info');
      }}
    />
  );
}

const CARD = {
  display: 'flex' as const, flexDirection: 'column' as const, gap: 12, padding: 14,
  border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)',
};
const MONO = { textAlign: 'right' as const, fontFamily: 'var(--font-mono)' };

export interface ElementPanelProps {
  template: LabelTemplate;
  index: number;
  /** Gabarit standard : on restitue, on ne règle pas. */
  locked: boolean;
  snap: boolean;
  /** La valeur réelle de ce champ sur l'article d'aperçu — sert la mesure de débordement. */
  sampleText: string;
  /** L'article d'aperçu n'a pas de valeur pour ce champ — sur la planche, il est en fantôme. */
  emptyOnSample?: boolean;
  onPatch: (patch: Partial<LabelElement>) => void;
  onRemove: () => void;
}

export function ElementPanel({ template, index, locked, snap, sampleText, emptyOnSample = false, onPatch, onRemove }: ElementPanelProps) {
  const el = template.elements[index];
  if (!el) return null;
  const barcode = el.field === FIELD_BARCODE;
  // ⚠️ La qualité se mesure sur CE code-barres : on présente au cœur un gabarit réduit à cet élément,
  //    plutôt que de dupliquer sa recherche du champ `barcode`.
  const quality = barcode ? templateBarcodeQuality({ ...template, elements: [el] }) : null;
  const fit = barcode ? null : fitInBox(el, sampleText);

  return (
    <div style={CARD}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{labelFieldName(el.field)}</span>
        {/* ⚠️ LE FANTÔME SE NOMME AUSSI ICI : sur la planche, le nom grisé dit « vide » ; le panneau dit ce que ça veut dire
               à l'impression, pour qu'on ne croie pas que le nom du champ sortira. */}
        {emptyOnSample && (
          <Tooltip content={t('Cet article n’a pas de valeur pour ce champ — rien ne s’imprimera à cet endroit.')}>
            <Badge tone="neutral" srLabel={t('Vide sur cet article : rien ne s’imprimera à cet endroit.')}>{t('vide sur cet article')}</Badge>
          </Tooltip>
        )}
        {!locked && (
          <button
            type="button"
            aria-label={t('Retirer cet élément')}
            title={t('Retirer cet élément')}
            onClick={onRemove}
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--ink-500)', padding: 2 }}
          >
            <Icon name="trash" size={14} />
          </button>
        )}
      </div>

      {locked
        ? <LockedReadout element={el} quality={quality} fit={fit} />
        // ⚠️ RENDUS PAR ÉLÉMENT (`key`) : passer d'un élément à l'autre ne doit pas laisser dans une case la mesure du
        //    précédent — deux éléments à la même position partageraient sinon le même champ.
        : <FreeControls key={index} template={template} element={el} snap={snap} onPatch={onPatch} quality={quality} fit={fit} />}
    </div>
  );
}

/** Ce que porte un élément d'un gabarit standard — restitué, jamais offert à la saisie. */
function LockedReadout({ element, quality, fit }: {
  element: LabelElement;
  quality: { scannable: boolean; pct: number; moduleMm: number } | null;
  fit: { free: boolean; over: boolean; neededMm: number; boxMm: number } | null;
}) {
  const width = elementWidthMm(element);
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <ReadOnlyValue label={t('X — mm')} value={mm(element.xMm)} locked />
        <ReadOnlyValue label={t('Y — mm')} value={mm(element.yMm)} locked />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <ReadOnlyValue label={element.field === FIELD_BARCODE ? t('Largeur des barres — mm') : t('Largeur de boîte')} value={width ? mm(width) : t('Libre')} locked />
        <ReadOnlyValue label={element.field === FIELD_BARCODE ? t('Hauteur des barres — mm') : t('Corps — mm')} value={mm(element.sizeMm)} locked />
      </div>
      <ReadOnlyValue
        label={t('Alignement')}
        value={element.align === 'center' ? t('Centre') : element.align === 'right' ? t('Droite') : t('Gauche')}
        locked
        hint={element.bold ? t('Texte en gras.') : undefined}
      />
      {quality && <BarcodeQuality {...quality} />}
      {fit && <TextFit fit={fit} />}
    </>
  );
}

/** Les réglages d'un gabarit libre. */
function FreeControls({ template, element, snap, onPatch, quality, fit }: {
  template: LabelTemplate;
  element: LabelElement;
  snap: boolean;
  onPatch: (patch: Partial<LabelElement>) => void;
  quality: { scannable: boolean; pct: number; moduleMm: number } | null;
  fit: { free: boolean; over: boolean; neededMm: number; boxMm: number } | null;
}) {
  const barcode = element.field === FIELD_BARCODE;

  const nudge = (dx: number, dy: number) => {
    const step = nudgeStepMm(snap);
    const p = clampPosition(
      template,
      Math.round((element.xMm + dx * step) * 10) / 10,
      Math.round((element.yMm + dy * step) * 10) / 10,
    );
    onPatch({ xMm: p.xMm, yMm: p.yMm });
  };

  /** La position que l'élément prendrait pour cette coordonnée — gardée dans l'étiquette. */
  const position = (key: 'xMm' | 'yMm', v: number) =>
    clampPosition(template, key === 'xMm' ? v : element.xMm, key === 'yMm' ? v : element.yMm);
  const coordinate = (key: 'xMm' | 'yMm') => ({
    clamp: (v: number) => position(key, v)[key],
    onCommit: (v: number) => { const p = position(key, v); onPatch({ xMm: p.xMm, yMm: p.yMm }); },
  });

  /** La hauteur (le corps d'un texte), bornée pour ce champ. */
  const height = {
    clamp: (v: number) => clampSize(template, element.field, elementWidthMm(element), v).sizeMm,
    onCommit: (sizeMm: number) => onPatch({ sizeMm }),
  };

  /** Une largeur de boîte, entre son minimum lisible et la largeur de l'étiquette. */
  const width = (min: number) => ({
    clamp: (v: number) => Math.max(min, Math.min(template.widthMm, v)),
    onCommit: (widthMm: number) => onPatch({ widthMm }),
  });

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <MmField label={t('X — mm')} value={element.xMm} {...coordinate('xMm')} />
        <MmField label={t('Y — mm')} value={element.yMm} {...coordinate('yMm')} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 5 }}>
        <NudgeButton icon="chevronL" label={t('Déplacer à gauche')} onClick={() => nudge(-1, 0)} />
        <NudgeButton icon="chevronU" label={t('Déplacer vers le haut')} onClick={() => nudge(0, -1)} />
        <NudgeButton icon="chevronD" label={t('Déplacer vers le bas')} onClick={() => nudge(0, 1)} />
        <NudgeButton icon="chevronR" label={t('Déplacer à droite')} onClick={() => nudge(1, 0)} />
      </div>

      {barcode ? (
        <>
          {/* ⚠️ « DES BARRES », PAS « LARGEUR » SEUL : le panneau du gabarit porte déjà « Largeur — mm » et « Hauteur — mm »
              sur la même page — deux champs du même nom qui ne règlent pas la même chose, et une aide technique ne les
              distingue pas. */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <MmField label={t('Largeur des barres — mm')} value={elementWidthMm(element)} {...width(MIN_BARCODE_WIDTH_MM)} />
            <MmField label={t('Hauteur des barres — mm')} value={element.sizeMm} {...height} />
          </div>
          {quality && <BarcodeQuality {...quality} />}
        </>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
            {/* ⚠️ VIDER, C'EST « LIBRE », pas zéro : une boîte de 0 mm ferait disparaître le texte. */}
            <MmField label={t('Largeur de boîte — mm')} placeholder={t('libre')} value={element.widthMm}
              onEmpty={() => onPatch({ widthMm: undefined })} {...width(MIN_TEXT_WIDTH_MM)} />
            <Button size="sm" onClick={() => onPatch({ widthMm: undefined })}>{t('Libre')}</Button>
          </div>
          {fit && <TextFit fit={fit} />}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
            <MmField label={t('Corps — mm')} value={element.sizeMm} {...height} />
            <Button size="sm" variant={element.bold ? 'primary' : 'ghost'} onClick={() => onPatch({ bold: !element.bold })}>
              
              {t('Gras')}
            </Button>
          </div>
        </>
      )}

      <div>
        <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 500, color: 'var(--ink-500)', marginBottom: 4 }}>{t('Alignement')}</span>
        <Segmented
          label={t('Alignement')}
          options={[{ id: 'left', label: t('Gauche') }, { id: 'center', label: t('Centre') }, { id: 'right', label: t('Droite') }]}
          value={element.align}
          onChange={(v) => onPatch({ align: v as LabelAlign })}
          size="sm"
        />
      </div>
    </>
  );
}

/**
 * Une flèche de déplacement au dixième (ou au demi) de millimètre.
 * ⚠️ BOUTON PUREMENT ICÔNIQUE, ce que le socle refuse à juste titre pour une action nommée : ici la
 *    flèche EST le sens, et son `aria-label` porte le mot. Quatre boutons étiquetés « Gauche / Haut /
 *    Bas / Droite » prendraient toute la colonne de réglages pour ne rien dire de plus.
 */
function NudgeButton({ icon, label, onClick }: { icon: IconName; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      style={{
        width: 34, height: 34, border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
        background: 'var(--paper)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 0, cursor: 'pointer',
      }}
    >
      <Icon name={icon} size={14} />
    </button>
  );
}

/**
 * L'INDICATEUR DE QUALITÉ EAN-13 — la pièce la plus précieuse du module.
 * ⚠️ Un code-barres trop étroit ne se voit PAS à l'écran : il se découvre en caisse, sur un article
 * qu'on ne peut plus scanner. Cet encart est la seule chose qui l'empêche.
 * ⚠️ IL DIT AUSSI QUAND TOUT VA BIEN. Un indicateur qui ne parle qu'en cas de problème laisse croire,
 * le reste du temps, que rien n'est vérifié — et on cesse de le chercher quand il compte.
 */
export function BarcodeQuality({ scannable, pct, moduleMm }: { scannable: boolean; pct: number; moduleMm: number }) {
  const message = !scannable
    ? t('Trop étroit pour être scanné de façon fiable — {pct} % de la taille nominale, minimum 80 %.', { pct })
    : pct < 100
      ? t('Lisible — {pct} % de la taille nominale, dans la plage admise.', { pct })
      : t('Confortable — {pct} % de la taille nominale.', { pct });
  const tone = scannable
    ? { bg: 'var(--teal-100)', bd: 'var(--teal-300)', fg: 'var(--teal-800)' }
    : { bg: 'var(--amber-bg)', bd: 'var(--amber)', fg: 'var(--amber-fg)' };
  return (
    <div style={{ padding: '9px 11px', borderRadius: 'var(--r-xs)', background: tone.bg, border: `1px solid ${tone.bd}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
        <Icon name={scannable ? 'check' : 'alert'} size={13} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700, color: tone.fg }}>
          {t('Module {v} mm', { v: moduleMm.toFixed(3).replace('.', ',') })}</span>
      </div>
      <span style={{ fontSize: 'var(--t-note)', color: tone.fg, lineHeight: 1.45 }}>{message}</span>
    </div>
  );
}

/**
 * L'indicateur de débordement.
 * ⚠️ ESTIMATION ASSUMÉE, et l'écran ne prétend pas mieux : la largeur réelle dépend de la fonte du
 * navigateur. Il ALERTE, il ne garantit pas — c'est déjà la seule chance de voir qu'une désignation
 * sera coupée avant d'avoir imprimé le rouleau.
 */
export function TextFit({ fit }: { fit: { free: boolean; over: boolean; neededMm: number; boxMm: number } }) {
  const message = fit.free
    ? t('Largeur libre — le texte s’étend autant qu’il en a besoin. Fixez une largeur pour le contraindre.')
    : fit.over
      ? t('Le texte dépasse — il sera coupé à l’impression. Réduisez le corps ou élargissez la boîte.')
      : t('Le texte tient dans la boîte.');
  const measure = fit.free ? t('{v} mm', { v: mm(fit.neededMm) }) : t('{v} mm sur {v2} mm', { v: mm(fit.neededMm), v2: mm(fit.boxMm) });
  const tone = fit.free
    ? { bg: 'var(--ink-50)', bd: 'var(--ink-200)', fg: 'var(--ink-600)' }
    : fit.over
      ? { bg: 'var(--amber-bg)', bd: 'var(--amber)', fg: 'var(--amber-fg)' }
      : { bg: 'var(--teal-100)', bd: 'var(--teal-300)', fg: 'var(--teal-800)' };
  return (
    <div style={{ padding: '9px 11px', borderRadius: 'var(--r-xs)', background: tone.bg, border: `1px solid ${tone.bd}` }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700, color: tone.fg, marginBottom: 3 }}>{measure}</div>
      <span style={{ fontSize: 'var(--t-note)', color: tone.fg, lineHeight: 1.45 }}>{message}</span>
    </div>
  );
}
